import * as React from "react";
import {
  fillTemplateString,
  getLocalePercentage,
  getLocalePrice,
} from "../commons/utils/text-rendering";
import { BilledInterval } from "../commons/model/pricing/BilledInterval";
import TaxRate from "../commons/model/pricing/TaxRate";
import { CouponFrequency } from "../commons/model/pricing/CouponFrequency";
import CouponInput from "../commons/components/form/input/CouponInput";
import { CouponType } from "../commons/model/pricing/CouponType";
import Coupon from "../commons/model/pricing/Coupon";
import "./PricingField.scss";

interface PricingFieldProps {
  validateEndpoint: string;
  priceExcludingTaxInCents: number;
  currency: string;
  billedInterval: BilledInterval;
  trialPeriodDay: number;
  taxRate?: TaxRate;
  onCouponChanged: (couponId?: string) => void;
  setErrorMsg: (msg: string) => void;
}

interface PricingFieldState {
  coupon?: Coupon;
  discountPrice: number;
}

export default class PricingField extends React.Component<PricingFieldProps, PricingFieldState> {
  constructor(props: PricingFieldProps) {
    super(props);
    this.state = {
      discountPrice: 0,
    };
  }

  render() {
    const priceExcludingTaxes = this.props.priceExcludingTaxInCents * 0.01;
    let exclusiveTaxPrice = 0;
    let totalPriceAfterTrialIncludingTaxes = priceExcludingTaxes;
    if (this.props.taxRate && !this.props.taxRate.inclusive) {
      exclusiveTaxPrice =
        (priceExcludingTaxes - this.state.discountPrice) * this.props.taxRate.percentage * 0.01;
      totalPriceAfterTrialIncludingTaxes *= 1.0 + this.props.taxRate.percentage * 0.01;
    }
    const totalPriceIncludingTaxes = Math.max(
      0,
      priceExcludingTaxes + exclusiveTaxPrice - this.state.discountPrice
    );
    const currency = this.props.currency;
    const localePriceExcludingTaxes = getLocalePrice(priceExcludingTaxes, currency);
    const localeTaxPrice = getLocalePrice(exclusiveTaxPrice, currency);
    const localeTotalPriceIncludingTaxes = getLocalePrice(totalPriceIncludingTaxes, currency);
    const localeTotalPriceAfterDiscount = getLocalePrice(
      totalPriceAfterTrialIncludingTaxes,
      currency
    );

    return (
      <div>
        <div className="v-flex pricing">
          {this.props.trialPeriodDay > 0 && (
            <div className="trial-period label primary">
              <span id="trial-period">
                {fillTemplateString(i18n["Form.DoctorRegistration.Payment.Billing.TrialPeriod"], {
                  period: this.props.trialPeriodDay.toString(),
                })}
              </span>
            </div>
          )}
          <div className="price-detail">
            <div className="h-flex space-between no-wrap">
              <div>{i18n["Form.DoctorRegistration.Payment.Billing.PriceExcludingTaxes"]}</div>
              <div>{localePriceExcludingTaxes}</div>
            </div>

            <CouponInput
              validateEndpoint={this.props.validateEndpoint}
              priceExcludingTaxInCents={this.props.priceExcludingTaxInCents}
              currency={this.props.currency}
              discountPrice={this.state.discountPrice}
              coupon={this.state.coupon}
              onApplyCoupon={this.onApplyCoupon.bind(this)}
              setErrorMsg={this.props.setErrorMsg}
            />

            {this.props.taxRate && (
              <>
                <div className="h-flex space-between">
                  <div>
                    {i18n["Form.DoctorRegistration.Payment.Billing.Tax"]} (
                    {getLocalePercentage(this.props.taxRate.percentage)})
                  </div>
                  <div id="tax-price-label">{localeTaxPrice}</div>
                </div>
              </>
            )}
          </div>
          <div className="h-flex space-between total-price-line">
            <div>
              {i18n["Form.DoctorRegistration.Payment.Billing.Total"]} (
              {PricingField.getBilledFrequencyI18n(this.props.billedInterval)})
            </div>
            <div id="total-price-label" className="total-price">
              {localeTotalPriceIncludingTaxes}
            </div>
          </div>
          {this.state.coupon &&
            (this.state.coupon.frequency == CouponFrequency.ONCE ||
              this.state.coupon.frequency == CouponFrequency.MONTHLY) && (
              <>
                <div className="h-flex space-between total-price-after-discount-line">
                  <div>
                    {i18n["Form.DoctorRegistration.Payment.Billing.TotalAfterCouponPeriod"]} (
                    {PricingField.getBilledFrequencyI18n(this.props.billedInterval)})
                  </div>
                  <div id="total-price-after-discount-label" className="total-price-after-discount">
                    {localeTotalPriceAfterDiscount}
                  </div>
                </div>
              </>
            )}
        </div>
      </div>
    );
  }

  private onApplyCoupon(coupon?: Coupon) {
    this.setState(
      {
        discountPrice: PricingField.getDiscountPrice(
          this.props.priceExcludingTaxInCents * 0.01,
          coupon
        ),
        coupon: coupon,
      },
      () => this.props.onCouponChanged(coupon?.id)
    );
  }

  private static getDiscountPrice(priceExcludingTaxes: number, coupon?: Coupon) {
    if (coupon == null) {
      return 0;
    }
    switch (coupon.type) {
      case CouponType.PERCENTAGE:
        return priceExcludingTaxes * coupon.amount * 0.01;
      case CouponType.AMOUNT_IN_CENTS:
        return coupon.amount * 0.01;
      default:
        throw Error("Unknown coupon type: " + coupon.type);
    }
  }

  private static getBilledFrequencyI18n(billedInterval: BilledInterval) {
    switch (billedInterval) {
      case BilledInterval.DAY:
        return i18n["Form.DoctorRegistration.Payment.Billing.BilledDaily"];
      case BilledInterval.WEEK:
        return i18n["Form.DoctorRegistration.Payment.Billing.BilledWeekly"];
      case BilledInterval.MONTH:
        return i18n["Form.DoctorRegistration.Payment.Billing.BilledMonthly"];
      case BilledInterval.YEAR:
        return i18n["Form.DoctorRegistration.Payment.Billing.BilledAnnually"];
      default:
        throw Error("Unknown billed frequency: " + billedInterval);
    }
  }
}
